/**
 * WebSocket-соединение с сервером (BS §9, CC §4.5).
 *
 * Обёртка написана вручную намеренно (CLIENT_WEB.md §1.3): протокол задан
 * кадрами с полем `t`, нужен явный контроль `hello{cursor}` → `ready{replay_complete}`
 * и запрет backoff при 4401. Готовые библиотеки скрывают ровно это.
 */
import { transportWebSocket } from '@shared/api/transport'
import type { SyncEvent } from '@shared/api/types'

const WS_PATH = '/api/v1/ws'
const SUBPROTOCOL = 'app.v1'

/** CC §4.5: ping каждые 25 с при отсутствии исходящего трафика. */
const PING_INTERVAL_MS = 25_000
/** Два пропущенных pong — соединение считается мёртвым. */
const MISSED_PONG_LIMIT = 2

const BACKOFF_BASE_MS = 1000
const BACKOFF_MAX_MS = 60_000
const JITTER = 0.2

/** Не чаще 1 кадра / 3 с на диалог (BS §9.3). */
const TYPING_THROTTLE_MS = 3000
const VISIBLE_THROTTLE_MS = 3000
/** TTL подсказки active_chat — 60 с, обновляем раз в 45 с. */
const ACTIVE_CHAT_REFRESH_MS = 45_000

/* ===================================================================== */
/* Типы кадров                                                            */
/* ===================================================================== */

export interface ReadyFrame {
  cursor: string
  server_time: string
  replay_complete: boolean
  reset_required: boolean
}

export interface TypingFrame {
  conversation_id: string
  user_id: string
  state: 'start' | 'stop'
  expires_at: string
}

export interface PresenceFrame {
  user_id: string
  online: boolean
  last_seen_bucket?: 'recently' | 'today' | 'this_week' | 'long_ago'
}

export type ByeReason =
  | 'session_revoked'
  | 'consent_required'
  | 'replaced'
  | 'server_shutdown'
  | 'protocol_error'

export type SocketStatus = 'idle' | 'connecting' | 'replaying' | 'live' | 'reconnecting' | 'stopped'

export interface SocketCallbacks {
  onEvent: (event: SyncEvent) => void | Promise<void>
  onReady: (frame: ReadyFrame) => void | Promise<void>
  onTyping?: (frame: TypingFrame) => void
  onPresence?: (frame: PresenceFrame) => void
  onFrameError?: (code: string, message: string) => void
  onBye?: (reason: ByeReason) => void
  /** 4401 — сессия недействительна; backoff ЗАПРЕЩЁН (CC §4.5). */
  onSessionInvalid?: () => void
  /** 4403 — reconnect запрещён, нужен `GET /me` для разбора причины. */
  onForbidden?: () => void
  onStatusChange?: (status: SocketStatus) => void
}

export interface HelloParams {
  cursor: string | null
  deviceId: string | null
  appVersion: string
  activeConversationId: string | null
  visibleConversations: string[]
}

/* ===================================================================== */
/* Соединение                                                             */
/* ===================================================================== */

export class SyncSocket {
  private socket: WebSocket | null = null
  private status: SocketStatus = 'idle'
  private attempt = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private activeChatTimer: ReturnType<typeof setInterval> | null = null
  private missedPongs = 0
  private lastOutgoingAt = 0
  private stopped = true
  /** Приостановка автоматического reconnect (consent_required, 4401). */
  private suspended = false

  private lastTypingSentAt = new Map<string, number>()
  private lastVisibleSentAt = 0

  private activeConversationId: string | null = null
  private visibleConversations: string[] = []

  constructor(
    private readonly callbacks: SocketCallbacks,
    private readonly getHello: () => Promise<HelloParams>,
  ) {}

  /* --- Жизненный цикл ---------------------------------------------------- */

  start(): void {
    this.stopped = false
    this.suspended = false
    void this.connect()
  }

  stop(): void {
    this.stopped = true
    this.clearTimers()
    this.setStatus('stopped')

    if (this.socket) {
      const socket = this.socket
      this.socket = null
      socket.onopen = null
      socket.onmessage = null
      socket.onclose = null
      socket.onerror = null
      try {
        socket.close(1000, 'client stop')
      } catch {
        /* уже закрыт */
      }
    }
  }

  /** Приостановить автоподключение до явного `resume()` (SYS-05, SYS-07). */
  suspend(): void {
    this.suspended = true
    this.clearTimers()
    this.socket?.close(1000, 'suspended')
    this.socket = null
    this.setStatus('stopped')
  }

  resume(): void {
    if (!this.suspended) return
    this.suspended = false
    this.attempt = 0
    void this.connect()
  }

  getStatus(): SocketStatus {
    return this.status
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN
  }

  private setStatus(status: SocketStatus): void {
    if (this.status === status) return
    this.status = status
    this.callbacks.onStatusChange?.(status)
  }

  private clearTimers(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    if (this.pingTimer) clearInterval(this.pingTimer)
    if (this.activeChatTimer) clearInterval(this.activeChatTimer)
    this.reconnectTimer = null
    this.pingTimer = null
    this.activeChatTimer = null
  }

  /* --- Подключение --------------------------------------------------------- */

  private async connect(): Promise<void> {
    if (this.stopped || this.suspended) return
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) return

    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting')

    const origin = globalThis.location?.origin ?? ''
    const url = `${origin.replace(/^http/, 'ws')}${WS_PATH}`

    let socket: WebSocket
    try {
      socket = transportWebSocket(url, SUBPROTOCOL)
    } catch {
      this.scheduleReconnect()
      return
    }

    this.socket = socket
    this.missedPongs = 0

    socket.onopen = () => {
      void this.sendHello()
    }

    socket.onmessage = (event: MessageEvent) => {
      void this.handleFrame(event.data)
    }

    socket.onerror = () => {
      // Реальная причина придёт в onclose; здесь только гасим шум.
    }

    socket.onclose = (event: CloseEvent) => {
      this.handleClose(event)
    }
  }

  private async sendHello(): Promise<void> {
    const params = await this.getHello()
    this.activeConversationId = params.activeConversationId
    this.visibleConversations = params.visibleConversations

    this.setStatus('replaying')

    this.send({
      t: 'hello',
      v: 1,
      cursor: params.cursor,
      device_id: params.deviceId,
      client: 'web',
      app_version: params.appVersion,
      active_conversation_id: params.activeConversationId,
      visible_conversations: params.visibleConversations.slice(0, 50),
    })

    this.startPing()
    this.startActiveChatRefresh()
  }

  private async handleFrame(raw: unknown): Promise<void> {
    if (typeof raw !== 'string') return

    let frame: Record<string, unknown>
    try {
      frame = JSON.parse(raw) as Record<string, unknown>
    } catch {
      return
    }

    switch (frame['t']) {
      case 'ready': {
        const ready: ReadyFrame = {
          cursor: String(frame['cursor'] ?? ''),
          server_time: String(frame['server_time'] ?? ''),
          replay_complete: frame['replay_complete'] === true,
          reset_required: frame['reset_required'] === true,
        }

        if (ready.replay_complete) {
          this.attempt = 0
          this.setStatus('live')
        }

        await this.callbacks.onReady(ready)
        break
      }

      case 'event': {
        const event = frame['event'] as SyncEvent | undefined
        if (event) await this.callbacks.onEvent(event)
        break
      }

      case 'typing':
        this.callbacks.onTyping?.({
          conversation_id: String(frame['conversation_id'] ?? ''),
          user_id: String(frame['user_id'] ?? ''),
          state: frame['state'] === 'start' ? 'start' : 'stop',
          expires_at: String(frame['expires_at'] ?? ''),
        })
        break

      case 'presence': {
        const presence: PresenceFrame = {
          user_id: String(frame['user_id'] ?? ''),
          online: frame['online'] === true,
        }
        const bucket = frame['last_seen_bucket']
        if (typeof bucket === 'string') {
          presence.last_seen_bucket = bucket as PresenceFrame['last_seen_bucket']
        }
        this.callbacks.onPresence?.(presence)
        break
      }

      case 'pong':
        this.missedPongs = 0
        break

      case 'error':
        // Нефатально: соединение НЕ рвём (CC §4.5).
        this.callbacks.onFrameError?.(String(frame['code'] ?? 'unknown'), String(frame['message'] ?? ''))
        break

      case 'bye': {
        const reason = String(frame['reason'] ?? 'server_shutdown') as ByeReason
        this.callbacks.onBye?.(reason)

        // session_revoked и consent_required приостанавливают автоподключение.
        if (reason === 'session_revoked' || reason === 'consent_required') this.suspended = true
        break
      }

      default:
        // Неизвестный кадр игнорируется (правило открытых перечислений).
        break
    }
  }

  private handleClose(event: CloseEvent): void {
    this.clearTimers()
    this.socket = null

    // 4401 — сессия недействительна: backoff ЗАПРЕЩЁН (CC §4.5).
    if (event.code === 4401) {
      this.suspended = true
      this.setStatus('stopped')
      this.callbacks.onSessionInvalid?.()
      return
    }

    // 4403 — reconnect запрещён, причину выясняет `GET /me`.
    if (event.code === 4403) {
      this.suspended = true
      this.setStatus('stopped')
      this.callbacks.onForbidden?.()
      return
    }

    if (this.stopped || this.suspended) {
      this.setStatus('stopped')
      return
    }

    this.scheduleReconnect()
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.suspended) return

    this.attempt += 1
    this.setStatus('reconnecting')

    const exponential = Math.min(BACKOFF_BASE_MS * 2 ** (this.attempt - 1), BACKOFF_MAX_MS)
    const jitter = exponential * JITTER * (Math.random() * 2 - 1)
    const delay = Math.max(BACKOFF_BASE_MS, exponential + jitter)

    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      void this.connect()
    }, delay)
  }

  /** Немедленная попытка — при возврате вкладки в foreground (CC §4.5 п. 7). */
  reconnectNow(): void {
    if (this.stopped || this.suspended) return
    if (this.isConnected()) return

    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    this.attempt = 0
    void this.connect()
  }

  /* --- Heartbeat ------------------------------------------------------------ */

  private startPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer)

    this.pingTimer = setInterval(() => {
      if (!this.isConnected()) return

      // Ping только при отсутствии исходящего трафика.
      if (Date.now() - this.lastOutgoingAt < PING_INTERVAL_MS) return

      if (this.missedPongs >= MISSED_PONG_LIMIT) {
        this.socket?.close(4000, 'missed pongs')
        return
      }

      this.missedPongs += 1
      this.send({ t: 'ping' })
    }, PING_INTERVAL_MS)
  }

  private startActiveChatRefresh(): void {
    if (this.activeChatTimer) clearInterval(this.activeChatTimer)

    this.activeChatTimer = setInterval(() => {
      if (!this.isConnected()) return
      this.send({ t: 'active_chat', conversation_id: this.activeConversationId })
    }, ACTIVE_CHAT_REFRESH_MS)
  }

  /* --- Исходящие кадры ------------------------------------------------------- */

  private send(frame: Record<string, unknown>): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return
    try {
      this.socket.send(JSON.stringify(frame))
      this.lastOutgoingAt = Date.now()
    } catch {
      /* соединение умерло — обработается в onclose */
    }
  }

  /** Подсказка подавления push: открытый диалог на этом устройстве. */
  setActiveConversation(conversationId: string | null): void {
    if (this.activeConversationId === conversationId) return
    this.activeConversationId = conversationId
    this.send({ t: 'active_chat', conversation_id: conversationId })
  }

  /** Полная замена набора видимых диалогов (не дельта), ≤ 50, ≤ 1 кадр / 3 с. */
  setVisibleConversations(ids: readonly string[]): void {
    const next = ids.slice(0, 50)
    const same =
      next.length === this.visibleConversations.length &&
      next.every((id, index) => this.visibleConversations[index] === id)
    if (same) return

    const now = Date.now()
    if (now - this.lastVisibleSentAt < VISIBLE_THROTTLE_MS) return

    this.lastVisibleSentAt = now
    this.visibleConversations = next
    this.send({ t: 'visible', conversation_ids: next })
  }

  sendTyping(conversationId: string, state: 'start' | 'stop'): void {
    const now = Date.now()
    const last = this.lastTypingSentAt.get(conversationId) ?? 0

    // `stop` отправляем всегда: он гасит индикатор у собеседника.
    if (state === 'start' && now - last < TYPING_THROTTLE_MS) return

    this.lastTypingSentAt.set(conversationId, now)
    this.send({ t: 'typing', conversation_id: conversationId, state })
  }

  /** Квитанции по WS — допустимая замена `POST /receipts` (CC §4.5). */
  sendReceipt(conversationId: string, cursors: { delivered_seq?: number; read_seq?: number }): boolean {
    if (!this.isConnected()) return false
    this.send({ t: 'receipt', conversation_id: conversationId, ...cursors })
    return true
  }
}
